import { h } from 'preact';
import useFormStore from '../../store/useFormStore';
import PromotionCard from './PromotionCard';
import PromotionCodeSearch from './PromotionsCodeSearch';

/**
 * Reusable layout component for multi-step form screens
 *
 * @param {Object} props
 * @param {boolean} [props.showPromoSearch=true] - Whether to show the promotion code search box
 * @param {boolean} [props.showOnlyApplied=true] - Whether to show only applied promotions
 * @param {boolean} [props.openPromoAccordion=false] - Whether to have the promotions accordion open by default
 */
export default function PromotionsBlock({
  showPromoSearch = true,
  showOnlyApplied = false,
  openPromoAccordion = false,
}) {
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const appliedPromotions = useFormStore((state) => state.appliedPromotions);
  const selectedServices = useFormStore((state) => state.selectedServices);
  const manuallyEnteredPromoCodes = useFormStore((state) => state.manuallyEnteredPromoCodes);
  const removeManualPromoCode = useFormStore((state) => state.removeManualPromoCode);


  if (!currentFranchise?.promotions || currentFranchise.promotions.length === 0) {
    return null;
  }

  // Get automatic promotions (not excluded)
  const getAutomaticPromotions = () => {
    return currentFranchise.promotions
      // Check for falsy exclude values (false, 0, "0", null, undefined)
      .filter(promo => promo.exclude !== true && promo.exclude !== "1" && promo.exclude !== 1)
      .filter(promo => {
        if (!selectedServices?.length) return true;
        if (!promo.service || typeof promo.service !== 'object') return false;
        const promoServiceIds = Object.keys(promo.service);
        return promoServiceIds.some(id => selectedServices.includes(id));
      });
  };

  // Get manually entered promotions
  const getManualPromotions = () => {
    return currentFranchise.promotions
      .filter(promo => manuallyEnteredPromoCodes.includes(promo.promo_code));
  };

  const automaticPromotions = getAutomaticPromotions();
  const manualPromotions = getManualPromotions();

  // Combine automatic and manual promotions (manual at the end)
  let allPromotions = [...automaticPromotions, ...manualPromotions];

  // Filter to only show applied promotions if requested
  if (showOnlyApplied) {
    allPromotions = allPromotions.filter(promo => appliedPromotions.includes(promo.promo_code));
  }

  return (
    <div className="bg-white p-6 shadow-sm">
      {/* Show message when no applied promotions and showOnlyApplied is true */}
      {showOnlyApplied && allPromotions.length === 0 && (
        <div className="text-sm text-gray-600 py-4">
          No promotions are currently applied to your order.
        </div>
      )}

      {/* Show all promotions (automatic + manually added) */}
      {allPromotions.length > 0 && (
        <details className="group border-b border-b-[#ccc] mb-3" open={openPromoAccordion}>
          <summary className="flex items-center justify-between cursor-pointer list-none text-gray-900 focus:outline-0 border-0">
            <div className="flex flex-col gap-1">
              <span className="text-lg font-semibold">
                {showOnlyApplied ? 'Applied Promotions' : 'Current Promotions'}
              </span>
              {!showOnlyApplied && (
                <span className="text-sm font-normal">
                  Coupons are applied automatically
                </span>
              )}
            </div>
            <svg
              className="w-5 h-5 transition-transform group-open:rotate-180"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>

          <div className="mt-4 flex flex-col gap-3.5 pb-2.5">
            {allPromotions.map((promo) => {
              const isApplied = appliedPromotions.includes(promo.promo_code);
              const isManuallyEntered = manuallyEnteredPromoCodes.includes(promo.promo_code);
              return (
                <PromotionCard
                  key={promo.id}
                  promo={promo}
                  isApplied={isApplied}
                  isManuallyEntered={isManuallyEntered}
                  onRemove={!showOnlyApplied && isManuallyEntered ? removeManualPromoCode : null}
                />
              );
            })}
          </div>
        </details>
      )}
      {showPromoSearch && !showOnlyApplied && <PromotionCodeSearch />}

      {/* Disclaimer */}
      <div className="text-xs text-gray-600 leading-relaxed">
        <p>
          * Minimum charge applies. Geographic restrictions may apply. Does not apply to service charge.
          Promotions are for Residential cleaning services only. Does not apply to emergency services.
          Some exclusions apply. Portable charges may apply.
        </p>
      </div>
    </div>
  );
}
